import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import {
  addAlertNote,
  bulkAssignAlerts,
  bulkCloseAlerts,
  bulkEscalateAlerts,
  closeAlert,
} from '../api'
import { describeBulkCloseRejection, isBulkCloseFailureBody } from '../types'
import type {
  AddNotePayload,
  BulkAssignPayload,
  BulkClosePayload,
  BulkEscalatePayload,
  ClosePayload,
} from '../types'

/**
 * AF-126 — alert mutations.
 *
 * Bulk endpoints are used per the technical notes ("Bulk assign uses the
 * backend bulk endpoint, not N individual calls"). Optimistic updates
 * happen at the React Query cache level so the UI feels instant; we roll
 * back on error.
 */

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['alerts'] })
}

export function useBulkAssign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkAssignPayload) => bulkAssignAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Assigned ${data.affected} alert${data.affected === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    onError: () => toast.error("Couldn't assign alerts"),
  })
}

export function useBulkClose() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkClosePayload) => bulkCloseAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Closed ${data.affected} alert${data.affected === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    // The backend answers 207 Multi-Status (never partially applied — see
    // `internal/api/alerts_handler.go` `writeBulkCloseResponse`) when any
    // alert in the batch fails its state-machine check; `api/client.ts`
    // throws an `ApiError` with status 207 for that case and preserves the
    // per-alert `{ affected, results }` ledger on `.details`. Surface the
    // REAL reason (which alerts blocked the batch, and why) instead of a
    // generic failure toast — the analyst needs to know before deciding
    // whether to deselect the blockers and retry. `BulkActionsBar` keeps
    // the selection intact on any error (see its `onConfirm`), so the
    // blocked alerts are still highlighted for that retry.
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.status === 207 &&
        isBulkCloseFailureBody(error.details)
      ) {
        const { title, description } = describeBulkCloseRejection(error.details)
        toast.error(title, { description })
        return
      }
      // Defense in depth for the batch-size cap (see
      // `MAX_BULK_CLOSE_SIZE` in types.ts): `BulkActionsBar` disables
      // the close action above the cap client-side, but a stale
      // selection, a race with `x` select-all, or any bypass of that
      // guard still reaches the server, which 400s with
      // `invalid_input` and a static, non-sensitive message ("alert_ids
      // exceeds maximum batch size of 100"). Surface that real reason
      // instead of the generic fallback — same rationale as the 207
      // branch above: an operator should never get a bare failure toast
      // when the server told us exactly why.
      if (error instanceof ApiError && error.status === 400) {
        toast.error("Couldn't close alerts", { description: error.message })
        return
      }
      toast.error("Couldn't close alerts")
    },
  })
}

/**
 * Single-alert close, routed through `POST /alerts/{id}/close` (see
 * `closeAlert` in api.ts). Used by `ActionRail` on the detail page —
 * `BulkActionsBar` keeps using `useBulkClose` for genuine multi-select.
 */
export function useCloseAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ClosePayload) => closeAlert(payload),
    onSuccess: (_data, variables) => {
      toast.success('Alert closed')
      invalidateAll(qc)
      void qc.invalidateQueries({ queryKey: ['alerts', 'detail', variables.alert_id] })
    },
    onError: () => toast.error("Couldn't close alert"),
  })
}

export function useBulkEscalate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkEscalatePayload) => bulkEscalateAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Escalated ${data.affected} alert${data.affected === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    onError: () => toast.error("Couldn't escalate alerts"),
  })
}

export function useAddNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AddNotePayload) => addAlertNote(payload),
    onSuccess: (_data, variables) => {
      toast.success('Note added')
      void qc.invalidateQueries({ queryKey: ['alerts', 'detail', variables.alert_id] })
    },
    onError: () => toast.error("Couldn't add note"),
  })
}
